/**
 * Training durchfuehren (F-34 B) - rein und getestet: Protokoll aus dem Plan
 * anlegen (mit den Werten vom letzten Mal), naechster offener Satz, Dauer und
 * Tempo.
 */
import type { LocalDate } from '@/domain/types';
import type {
  PlanEinheit, PlanSatz, ProtokollSatz, ProtokollUebung, TrainingsProtokoll, Uebung,
} from '@/domain/training/typen';

/** Saetze der letzten fertigen Einheit vor `datum`, in der die Uebung vorkam. */
export function letzteSaetze(protokolle: TrainingsProtokoll[], uebungId: string, vor: LocalDate, ohneId?: string): ProtokollSatz[] | null {
  const kandidaten = protokolle
    .filter((p) => p.status === 'fertig' && p.id !== ohneId && p.datum <= vor)
    .sort((a, b) => b.datum.localeCompare(a.datum) || (b.ende ?? '').localeCompare(a.ende ?? ''));
  for (const p of kandidaten) {
    const u = p.uebungen.find((x) => x.uebungId === uebungId && !x.uebersprungen);
    const gemacht = u?.saetze.filter((s) => s.erledigt);
    if (gemacht?.length) return gemacht;
  }
  return null;
}

function satzAusPlan(ziel: PlanSatz, letzt: ProtokollSatz | undefined): ProtokollSatz {
  const s: ProtokollSatz = { erledigt: false, ziel };
  if (ziel.art) s.art = ziel.art;
  // Vorschlag: Gewicht vom letzten Mal (gleicher Satz), sonst laut Plan; Wiederholungen laut Plan.
  const gewicht = letzt?.gewicht ?? ziel.gewicht;
  if (gewicht) s.gewicht = gewicht;
  const wdh = ziel.wdh ?? ziel.wdhBis ?? letzt?.wdh;
  if (wdh) s.wdh = wdh;
  const dauer = ziel.dauerSek ?? letzt?.dauerSek;
  if (dauer) s.dauerSek = dauer;
  return s;
}

/** Uebungen des Protokolls aus der geplanten Einheit. */
export function uebungenAusPlan(e: PlanEinheit, protokolle: TrainingsProtokoll[], datum: LocalDate): ProtokollUebung[] {
  return e.uebungen.map((pu) => {
    const letzte = letzteSaetze(protokolle, pu.uebungId, datum) ?? [];
    const letzteArbeit = letzte.filter((s) => s.art !== 'aufwaermen');
    let arbeitIndex = 0;
    const u: ProtokollUebung = {
      id: pu.id,
      uebungId: pu.uebungId,
      saetze: pu.saetze.map((z) => satzAusPlan(z, z.art === 'aufwaermen' ? undefined : letzteArbeit[arbeitIndex++])),
    };
    if (pu.pauseSek !== undefined) u.pauseSek = pu.pauseSek;
    if (pu.block) u.block = pu.block;
    if (pu.stufe) u.stufe = pu.stufe;
    if (pu.notiz) u.notiz = pu.notiz;
    return u;
  });
}

/** Saetze einer Ersatzuebung: gleiche Anzahl und Ziele, Gewicht vom letzten Mal der neuen Uebung. */
export function uebungTauschen(u: ProtokollUebung, neu: Uebung, protokolle: TrainingsProtokoll[], datum: LocalDate): ProtokollUebung {
  const letzte = (letzteSaetze(protokolle, neu.id, datum) ?? []).filter((s) => s.art !== 'aufwaermen');
  let i = 0;
  return {
    ...u,
    uebungId: neu.id,
    ersetztVon: u.ersetztVon ?? u.uebungId,
    saetze: u.saetze.map((s) => {
      if (s.erledigt) return s;
      const ziel = s.ziel ?? {};
      const n = satzAusPlan(ziel, s.art === 'aufwaermen' ? undefined : letzte[i++]);
      if (!letzte.length) delete n.gewicht;
      return n;
    }),
  };
}

/** Wo geht es weiter? Erster offener Satz einer nicht uebersprungenen Uebung. */
export function naechsterSatz(p: Pick<TrainingsProtokoll, 'uebungen'>): { uebung: number; satz: number } | null {
  for (let ui = 0; ui < p.uebungen.length; ui++) {
    const u = p.uebungen[ui]!;
    if (u.uebersprungen) continue;
    const si = u.saetze.findIndex((s) => !s.erledigt);
    if (si >= 0) return { uebung: ui, satz: si };
  }
  return null;
}

/**
 * Pause nach einem Satz: Im Supersatz erst nach der letzten Uebung der Runde.
 * `null` = keine Pause (z. B. zur naechsten Uebung im Supersatz wechseln).
 */
export function pauseNach(p: Pick<TrainingsProtokoll, 'uebungen'>, ui: number, bibliothek: ReadonlyMap<string, Uebung>, standard = 90): number | null {
  const u = p.uebungen[ui];
  if (!u) return null;
  const naechste = p.uebungen[ui + 1];
  if (u.block && naechste?.block === u.block) return null;
  return u.pauseSek ?? bibliothek.get(u.uebungId)?.standard.pauseSek ?? standard;
}

/** Fortschritt: erledigte und alle Arbeitssaetze (uebersprungene Uebungen zaehlen nicht). */
export function fortschritt(p: Pick<TrainingsProtokoll, 'uebungen'>): { erledigt: number; gesamt: number } {
  const saetze = p.uebungen.filter((u) => !u.uebersprungen).flatMap((u) => u.saetze);
  return { erledigt: saetze.filter((s) => s.erledigt).length, gesamt: saetze.length };
}

/** Tatsaechliche Dauer in Minuten: von Hand > Start bis Ende > Laufzeit. */
export function protokollMinuten(p: Pick<TrainingsProtokoll, 'dauerMin' | 'start' | 'ende' | 'ausdauer'>): number {
  if (p.dauerMin) return p.dauerMin;
  if (p.start && p.ende) return Math.max(1, Math.round((Date.parse(p.ende) - Date.parse(p.start)) / 60_000));
  if (p.ausdauer?.dauerSek) return Math.round(p.ausdauer.dauerSek / 60);
  return 0;
}

/** Tempo in Sekunden je km. */
export function pace(distanzKm?: number, dauerSek?: number): number | undefined {
  return distanzKm && dauerSek ? Math.round(dauerSek / distanzKm) : undefined;
}

/** „45:30“, „1:05:00“, „45“ (Minuten) -> Sekunden. */
export function dauerLesen(s: string): number | undefined {
  const t = s.trim();
  if (!t) return undefined;
  const teile = t.split(':').map(Number);
  if (teile.some((x) => !Number.isFinite(x) || x < 0)) return undefined;
  if (teile.length === 1) return Math.round(teile[0]! * 60) || undefined;
  if (teile.length === 2) return teile[0]! * 60 + teile[1]!;
  if (teile.length === 3) return teile[0]! * 3600 + teile[1]! * 60 + teile[2]!;
  return undefined;
}

export function dauerText(sek?: number): string {
  if (!sek) return '';
  const h = Math.floor(sek / 3600);
  const m = Math.floor((sek % 3600) / 60);
  const s = sek % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}
